import { memo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { Button } from 'components/common';
import { Icon } from 'components/common/icons';
import {
  Form,
  FormGroup,
  RadioGroup,
  Select,
  Text
} from 'components/common/form';
import {
  Modal,
  ModalContent,
  ModalFooter,
  ModalPortal
} from 'components/common/modal';
import { useEditorContext, useEditorDispatch } from 'components/editor';
import { editorSocket } from 'components/editor/socket';

import { FormApi } from 'informed';

const EditorTransferBlocks = () => {
  const { dispatch } = useDispatch();

  const {
    editorBlocks,
    editorBlocksOrder,
    editorChapters,
    editorCurrentChapter,
    editorTransferBlocksLoading,
    editorTransferBlocksSuccess,
    editorTransferBlocksVisible
  } = useEditorContext();

  const editorDispatch = useEditorDispatch();

  const {
    messages,
    pageState: { course },
    theme
  } = useReduxState();

  const formApi = useRef<FormApi>(undefined);

  const router = useRouter();

  const [targetChapterId, setTargetChapterId] = useState(null);

  const handleSubmit = async (values) => {
    const selectedBlockIds = editorBlocksOrder.filter(
      (id) => editorBlocks[id].isSelected
    );

    if (values.chooseTransferAction === 'new') {
      try {
        const newChapter = await api.post(
          `/admin/courses/${course.id}/chapters`,
          { title: values.newChapterTitle }
        );

        values.chapterId = newChapter.data.chapter.id;

        const chapters = await api.get(`/admin/courses/${course.id}/chapters`);

        editorDispatch({
          type: 'updateContext',
          update: {
            editorChapters: chapters.data.chapters
          }
        });
      } catch (err) {
        await dispatch.handleErrorDispatch(err);

        hideModal();
        return false;
      }
    }

    setTargetChapterId(values.chapterId);

    editorSocket.transferBlocks({
      chapterId: editorCurrentChapter.id,
      blockIds: selectedBlockIds,
      targetChapterId: values.chapterId
    });
  };

  const hideModal = () => {
    formApi.current?.reset();

    editorDispatch({
      type: 'updateContext',
      update: {
        editorTransferBlocksLoading: false,
        editorTransferBlocksSuccess: false,
        editorTransferBlocksVisible: false
      }
    });
  };

  const moveToChapter = () => {
    editorDispatch({
      type: 'updateContext',
      update: {
        editorTransferBlocksLoading: false,
        editorTransferBlocksSuccess: false,
        editorTransferBlocksVisible: false
      }
    });

    router.push(`/admin/courses/${course.slug}/edit/${targetChapter?.slug}`);
  };

  const chapterOptions = editorChapters.filter(
    (option) => option.id !== editorCurrentChapter.id
  );

  const targetChapter = targetChapterId
    ? editorChapters.find((c) => c.id === targetChapterId)
    : null;

  return (
    <>
      <ModalPortal>
        <Form
          getApi={(form) => (formApi.current = form)}
          onSubmit={handleSubmit}
        >
          {({ formState }) => (
            <Modal
              onHide={hideModal}
              title={messages.editorHeaderTransferBlocks}
              visible={editorTransferBlocksVisible}
            >
              <ModalContent>
                {!editorTransferBlocksSuccess ? (
                  <div>
                    <FormGroup
                      label={messages.editorHeaderTransferBlocksChoose}
                    >
                      <RadioGroup
                        field="chooseTransferAction"
                        initialValue={chapterOptions.length ? 'exist' : 'new'}
                        options={[
                          {
                            disabled: !chapterOptions.length,
                            id: 'chooseTransferAction-move-exist',
                            label:
                              messages.editorHeaderTransferBlocksWhereExist,
                            value: 'exist'
                          },
                          {
                            id: 'chooseTransferAction-move-new',
                            label: messages.editorHeaderTransferBlocksWhereNew,
                            value: 'new'
                          }
                        ]}
                      />
                    </FormGroup>
                    {formState.values.chooseTransferAction === 'exist' ? (
                      <FormGroup
                        label={messages.editorHeaderTransferBlocksWhere}
                      >
                        <Select
                          field="chapterId"
                          id="selected-blocks-move-chapter-id"
                          initialValue={chapterOptions[0].id}
                          isSearchable={true}
                          options={chapterOptions.map((chapter) => ({
                            label: chapter.title,
                            value: chapter.id
                          }))}
                        />
                      </FormGroup>
                    ) : (
                      <FormGroup label={messages.editChapterTitle}>
                        <Text field="newChapterTitle" id="new-chapter-title" />
                      </FormGroup>
                    )}
                  </div>
                ) : (
                  <div className="success">
                    <div className="success-icon">
                      <Icon icon="check" />
                    </div>
                    <div className="success-text">
                      {messages.editorHeaderTransferBlocksSuccess}
                    </div>

                    <div className="success-actions">
                      <Button
                        category="default"
                        onClick={moveToChapter}
                        style={{ width: '100%' }}
                        text={`${messages.editorHeaderGoToChapter} ${targetChapter?.title}`}
                      />

                      <Button
                        category="light"
                        onClick={hideModal}
                        style={{ marginTop: 15, width: '100%' }}
                        text={messages.editorHeaderContinue}
                      />
                    </div>

                    <style jsx>{`
                      .success {
                        text-align: center;
                      }
                      .success-icon {
                        background-color: ${theme.successColor};
                        border-radius: 50%;
                        color: ${theme.successTextColor};
                        display: inline-block;
                        font-size: 1rem;
                        height: 26px;
                        margin-top: 10px;
                        padding: 7px 0;
                        text-align: center;
                        width: 26px;
                      }
                      .success-text {
                        margin-top: 10px;
                      }
                      .success-actions {
                        margin-top: 20px;
                      }
                      .success-or {
                        margin: 5px 0;
                      }
                    `}</style>
                  </div>
                )}
              </ModalContent>

              {!editorTransferBlocksSuccess ? (
                <ModalFooter>
                  <Button
                    category="light"
                    onClick={hideModal}
                    text={messages.commonCancel}
                  />
                  <Button
                    category="success"
                    disabled={
                      formState.values.chooseTransferAction === 'new' &&
                      !formState.values.newChapterTitle
                    }
                    loading={editorTransferBlocksLoading}
                    text={messages.commonMove}
                    type="submit"
                  />
                </ModalFooter>
              ) : null}
            </Modal>
          )}
        </Form>
      </ModalPortal>
    </>
  );
};

export default memo(EditorTransferBlocks);
